import { errorAction, successAction, types } from "@authzsvc/api-lib";
import { Button } from "antd";
import PropTypes from "prop-types";
import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import {
  subscribeCurrentIdentityToGroups,
  unsubscribeCurrentIdentityFromGroups,
  getCurrentIdentityGroups,
  getMyRequests,
} from "../../actions/api";

import { GroupSyncTypes } from "../../constants/enums";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "../../util";

const GroupSubscriptionAction = ({
  groupId,
  syncType,
  disabled,
  belongsToGroup,
  reason,
  toggleSubscription,
  handleRefreshMembers,
  closeModal,
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const groupIsSlave = () =>
    syncType !== GroupSyncTypes.Master && syncType !== GroupSyncTypes.NoSync;

  const displaySubscriptionMessage = useCallback(
    (data) => {
      if (
        data.type === errorAction(types.SUBSCRIBE_CURRENT_IDENTITY_TO_GROUPS)
      ) {
        showErrorNotification(
          data?.payload?.title ||
            "We're sorry! It wasn't possible to subscribe you.",
          data?.payload?.data?.message || ""
        );
      } else if (data.payload.data.status === "Pending") {
        showWarningNotification(
          "Subscription Pending",
          "Your request to join this group is pending approval. You will receive an email if it is accepted."
        );
      } else {
        toggleSubscription();
        showSuccessNotification(
          "",
          "You have successfully subscribed to the group."
        );
      }
    },
    [toggleSubscription]
  );

  const handleSubscribe = async () => {
    setLoading(true);
    const identityMembershipObject = [{ id: groupId, comment: reason }];
    const response = await dispatch(
      subscribeCurrentIdentityToGroups(identityMembershipObject)
    );

    if (response) {
      setLoading(false);
      dispatch(getCurrentIdentityGroups()).then(() => {
        displaySubscriptionMessage(response);
        handleRefreshMembers && handleRefreshMembers();
        closeModal && closeModal();
      });

      dispatch(getMyRequests());
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    const response = await dispatch(
      unsubscribeCurrentIdentityFromGroups([groupId])
    );

    if (
      response.type ===
      errorAction(types.UNSUBSCRIBE_CURRENT_IDENTITY_FROM_GROUPS)
    ) {
      showErrorNotification(
        response?.payload?.title || "Sorry, an error occurred",
        response?.payload?.data?.message || "Please try again later"
      );
    } else if (
      response.type ===
      successAction(types.UNSUBSCRIBE_CURRENT_IDENTITY_FROM_GROUPS)
    ) {
      toggleSubscription();
      handleRefreshMembers && handleRefreshMembers();
      showSuccessNotification(
        "",
        "You have successfully unsubscribed from the group."
      );
    }

    setLoading(false);
    dispatch(getCurrentIdentityGroups());
  };

  return belongsToGroup ? (
    <Button
      id="unsubscribeButton"
      type="primary"
      danger
      onClick={handleUnsubscribe}
      loading={loading}
      disabled={groupIsSlave() || disabled}
    >
      Unsubscribe
    </Button>
  ) : (
    <Button
      id="subscribeButton"
      type="primary"
      onClick={handleSubscribe}
      loading={loading}
      disabled={groupIsSlave() || disabled}
    >
      {groupIsSlave() ? "Please subscribe in Egroups" : "Subscribe"}
    </Button>
  );
};

GroupSubscriptionAction.propTypes = {
  groupId: PropTypes.string.isRequired,
  syncType: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  belongsToGroup: PropTypes.bool,
  toggleSubscription: PropTypes.func.isRequired,
  handleRefreshMembers: PropTypes.func,
  closeModal: PropTypes.func,
  reason: PropTypes.string,
};

export default GroupSubscriptionAction;
